import dayjs from "dayjs";
import "dayjs/locale/es";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// Configurar plugins y locale
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale("es");

/**
 * Obtiene la fecha y hora actual
 */
export function now(): dayjs.Dayjs {
  return dayjs();
}

/**
 * Crea una instancia de dayjs a partir de una fecha
 */
export function date(value?: string | number | Date | dayjs.Dayjs): dayjs.Dayjs {
  return dayjs(value);
}

/**
 * Formatea una fecha a formato español (DD/MM/YYYY)
 */
export function formatDate(
  value: string | number | Date | dayjs.Dayjs | null | undefined,
  format: string = "DD/MM/YYYY"
): string {
  if (!value) return "";
  return dayjs(value).format(format);
}

/**
 * Formatea una fecha con hora (DD/MM/YYYY HH:mm)
 */
export function formatDateTime(
  value: string | number | Date | dayjs.Dayjs | null | undefined,
  format: string = "DD/MM/YYYY HH:mm"
): string {
  if (!value) return "";
  return dayjs(value).format(format);
}

/**
 * Formatea una fecha a formato relativo (hace 2 días, hace 1 hora, etc.)
 */
export function formatRelative(
  value: string | number | Date | dayjs.Dayjs | null | undefined
): string {
  if (!value) return "";
  return dayjs(value).fromNow();
}

/**
 * Formatea una fecha a formato largo en español
 * Ejemplo: "lunes, 15 de enero de 2024"
 */
export function formatLongDate(
  value: string | number | Date | dayjs.Dayjs | null | undefined
): string {
  if (!value) return "";
  return dayjs(value).format("dddd, D [de] MMMM [de] YYYY");
}

/**
 * Convierte una fecha a Date nativo (útil para compatibilidad con APIs)
 */
export function toDate(value: string | number | Date | dayjs.Dayjs): Date {
  return dayjs(value).toDate();
}

/**
 * Verifica si una fecha es válida
 */
export function isValid(value: string | number | Date | dayjs.Dayjs | null | undefined): boolean {
  if (!value) return false;
  return dayjs(value).isValid();
}

/**
 * Suma tiempo a una fecha
 */
export function add(
  value: string | number | Date | dayjs.Dayjs,
  amount: number,
  unit: dayjs.ManipulateType = "day"
): dayjs.Dayjs {
  return dayjs(value).add(amount, unit);
}

/**
 * Resta tiempo a una fecha
 */
export function subtract(
  value: string | number | Date | dayjs.Dayjs,
  amount: number,
  unit: dayjs.ManipulateType = "day"
): dayjs.Dayjs {
  return dayjs(value).subtract(amount, unit);
}

/**
 * Verifica si una fecha es anterior a otra
 */
export function isBefore(
  value: string | number | Date | dayjs.Dayjs,
  compareTo: string | number | Date | dayjs.Dayjs
): boolean {
  return dayjs(value).isBefore(compareTo);
}

/**
 * Verifica si una fecha es posterior a otra
 */
export function isAfter(
  value: string | number | Date | dayjs.Dayjs,
  compareTo: string | number | Date | dayjs.Dayjs
): boolean {
  return dayjs(value).isAfter(compareTo);
}

/**
 * Obtiene la diferencia en días entre dos fechas
 */
export function diffInDays(
  from: string | number | Date | dayjs.Dayjs,
  to: string | number | Date | dayjs.Dayjs
): number {
  return dayjs(to).diff(dayjs(from), "day");
}

/**
 * Obtiene la diferencia en horas entre dos fechas
 */
export function diffInHours(
  from: string | number | Date | dayjs.Dayjs,
  to: string | number | Date | dayjs.Dayjs
): number {
  return dayjs(to).diff(dayjs(from), "hour");
}

/**
 * Combina los campos `date` y `time` de un partido del catálogo (config.matches)
 * en un timestamp ISO único. Retorna null si no hay datos válidos.
 */
export function parseCatalogMatchTimestamp(match: any): string | null {
  const matchDate = match?.date && !Number.isNaN(Date.parse(match.date))
    ? new Date(match.date)
    : null;
  const matchTime = match?.time && !Number.isNaN(Date.parse(match.time))
    ? new Date(match.time)
    : null;

  if (matchDate && matchTime) {
    const combined = new Date(matchDate);
    combined.setUTCHours(
      matchTime.getUTCHours(),
      matchTime.getUTCMinutes(),
      matchTime.getUTCSeconds(),
      0,
    );
    return combined.toISOString();
  }

  return (matchTime ?? matchDate)?.toISOString() ?? null;
}

// Exportar dayjs para uso avanzado si es necesario
export { dayjs };
